import { memo, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  GridLoader,
  SDrawer,
  SEmptyState,
  SVirtualGrid,
  SVirtualTable,
  TableHeaderProps,
  TableLoader
} from '@setvi/shared/components';
import { StringParam, useQueryParam } from 'use-query-params';
import { useGridLayout } from 'componentsV2/cards/layouts/grid';
import { Views } from 'Styles';
import { Customer } from 'Services/Mongo/Types';
import CenterBox from '@setvi/shared/components/scenter-box';
import { Box, CircularProgress, useTheme } from '@material-ui/core';
import { useVirtualizeData } from '@setvi/shared/components/s-table/hooks/useVirtualizeData';
import {
  ProductColumn,
  ProductPriceType,
  ProductGridItem,
  Product as ProductType,
  ProductDetailsTabsIds,
  PriceGroup,
  ProductStock
} from '@setvi/shared/interfaces';
import { ComposeEmailValues as ComposeValuesType } from 'providersV2/compose-email/hook';
import { ActionBar } from 'componentsV2';
import { pricingDependsOnCustomer } from 'componentsV2/product/details/content/components/header/components';
import {
  AdditionalContent,
  additionalContentHeight,
  Product,
  ProductDrawerContent,
  ProductLoader
} from './components';
import { useProductColums } from './useProductColumns';
import { calculateHeight } from './heplers';

export const AIProductMatchTitle = 'AI Reasoning';
export const productKeyGetter = (product: ProductType) => product?._id;

// Optional extras injected into each grid card (e.g. storefront cart actions)
export interface GridCardSlots {
  renderBadge?: (product: ProductType) => ReactNode;
  renderFooter?: (product: ProductType) => ReactNode;
  footerHeight?: number;
  /** Hides the status / stock chips (e.g. "Active", "Stocked - …") */
  hideStatusBadges?: boolean;
}

export interface ProductsListProps {
  statusId?: string;
  products: ProductType[];
  selectedCustomer?: Customer;
  selectedPriceGroup?: PriceGroup;
  segmentId?: string;
  isLoading: boolean;
  searchQuery?: string;
  selectedProducts?: ProductType[];
  composeEmailValues?: ComposeValuesType;
  drawerOpen: boolean;
  disableSelect: boolean;
  canShowActionBar?: boolean;
  productPricesLoading: boolean;
  feedback?: JSX.Element;
  config: {
    columns: ProductColumn[];
    grid: ProductGridItem;
    priceType: ProductPriceType;
    stock: ProductStock;
    manufacturerSku: ProductGridItem['manufacturerSku'];
  };
  isCustomerView?: boolean;
  aiSearchActive?: boolean;
  type?: 'none' | 'checkbox' | 'radio';
  fetchMore: () => void;
  updateProduct?: (
    product: ProductType,
    newValues: Partial<ProductType>
  ) => void;
  onPromotionClick?: (product: ProductType) => void;
  onGetProductPrice?: (product: ProductType[]) => Promise<void>;
  openProductDetails: (
    product: ProductType,
    defaultOpenedTab?: ProductDetailsTabsIds
  ) => void;
  onEmailProducts?: (products: ProductType[]) => void;
  setDrawerOpen: (value: boolean) => void;
  handleProductSelect?: (
    isChecked: boolean,
    selectedArray: ProductType[]
  ) => void;
  modifySelectedProduct?: (newSelected: ProductType[]) => void;
  handleEmail?: () => void;
  headerProps?: Partial<TableHeaderProps>;
  simpleLoading?: boolean;
  gridCard?: GridCardSlots;
}

export const disableSelectProductTooltip =
  'The maximum number of product selected';

export const ProductsList = memo(
  ({
    products,
    feedback,
    selectedCustomer,
    selectedPriceGroup,
    segmentId,
    isLoading,
    searchQuery,
    selectedProducts = [],
    composeEmailValues,
    drawerOpen,
    disableSelect,
    canShowActionBar = true,
    productPricesLoading,
    aiSearchActive,
    statusId,
    type = 'checkbox',
    config: { columns: configColumns, grid, priceType, stock },
    isCustomerView,
    openProductDetails,
    updateProduct,
    onPromotionClick,
    onGetProductPrice = () => Promise.resolve(),
    fetchMore,
    setDrawerOpen,
    handleProductSelect,
    modifySelectedProduct,
    handleEmail,
    simpleLoading,
    headerProps,
    gridCard
  }: ProductsListProps) => {
    const classes = useGridLayout();
    const [listItemHeight, setListItemHeight] = useState(90);

    // Check if any product-name element has height > 90px
    useEffect(() => {
      const checkProductNameHeights = () => {
        const productNameElements = document.querySelectorAll('.product-name');
        let needsLargerHeight = false;

        productNameElements.forEach(element => {
          if (element.clientHeight > 90) {
            needsLargerHeight = true;
          }
        });

        setListItemHeight(needsLargerHeight ? 110 : 90);
      };

      // Check after initial render and on resize
      const timeoutId = setTimeout(checkProductNameHeights, 100);
      window.addEventListener('resize', checkProductNameHeights);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', checkProductNameHeights);
      };
    }, [products]);

    const { columns } = useProductColums({
      searchQuery,
      productPricesLoading,
      // we hide price and gp columns when no customer is selected or if AI search is active
      // because we AI API don't have the data to show yet
      selectedCustomer,
      selectedPriceGroup,
      segmentId,
      configColumns,
      isCustomerView,
      stockRefresh: stock.refresh,
      updateProduct,
      openProductDetails,
      onGetProductPrice,
      onPromotionClick
    });

    const [currentOpenView] = useQueryParam('view', StringParam);
    const { spacing } = useTheme();
    const containerRef = useRef<HTMLDivElement>();
    const pricingList =
      selectedCustomer || selectedPriceGroup
        ? grid?.pricing
        : grid?.pricing?.filter(i => !pricingDependsOnCustomer.includes(i.id));

    const buttonLabel =
      composeEmailValues?.body?.trim()?.length > 0 ? 'Add to Email' : 'Email';

    const getGridItemHeight = () => {
      const baseHeight = 330;
      const leadTime = grid?.manufacturerLeadTime ? 15 : 0;
      const AIMatch = aiSearchActive ? 140 : 40;

      const additionalHeight = () => {
        if (priceType === ProductPriceType.Full) {
          const secondRowHeight =
            grid?.manufacturerSku || grid?.stock?.show ? 19 : 0;
          const divider = 17;

          if (!pricingList.length) return 4 + secondRowHeight;
          const height = calculateHeight(pricingList);

          return secondRowHeight + divider + height;
        }

        if (priceType === ProductPriceType.Get)
          return selectedCustomer ? 80 : 23;

        if (priceType === ProductPriceType.GetFull)
          return selectedCustomer ? 82 : 25;

        // List
        return 55;
      };

      const footer = gridCard?.renderFooter ? (gridCard.footerHeight ?? 0) : 0;
      // The chips row: badge (22) + its top margin (6)
      const statusBadges = gridCard?.hideStatusBadges ? 28 : 0;

      return (
        baseHeight +
        leadTime +
        additionalHeight() +
        AIMatch +
        footer -
        statusBadges
      );
    };

    const { matrix, listVirtualizer, columnVirtualizer } =
      useVirtualizeData<ProductType>({
        elements: products || [],
        view: (currentOpenView || Views.LIST_VIEW) as Views,
        count: products?.length || 0,
        parentRef: containerRef,
        itemSize:
          listItemHeight + (aiSearchActive ? additionalContentHeight - 5 : 0),
        gridItemHeight: getGridItemHeight(),
        gridItemWidth: 309,
        gap: Views.GRID_VIEW === currentOpenView ? 22 : 0,
        gapX: 16,
        gapY: Views.GRID_VIEW === currentOpenView ? 16 : 0,
        overscan: Views.GRID_VIEW === currentOpenView ? 2 : 5
      });

    useEffect(() => {
      const productWithPrice: ProductType[] = selectedProducts
        .map(i => {
          const price = products.find(
            j => productKeyGetter(i) === productKeyGetter(j)
          )?.price;

          return typeof price === 'number' ? { ...i, price } : null;
        })
        .filter(i => !!i);
      productWithPrice.length && modifySelectedProduct?.(productWithPrice);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products]);

    if (aiSearchActive && isLoading && !simpleLoading)
      return <ProductLoader statusId={statusId} />;

    if (aiSearchActive && isLoading && simpleLoading)
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%">
          <CircularProgress />
        </Box>
      );

    if (products.length === 0 && !isLoading) {
      const hasSearchQuery = searchQuery && searchQuery.length > 0;

      let content = {
        image: hasSearchQuery
          ? '/images/no-results.svg'
          : '/images/product-placeholder.svg',
        subTitle: hasSearchQuery
          ? 'No Results Found'
          : 'This category does not contain any products',
        title: !hasSearchQuery && 'No Products Available'
      };

      if (aiSearchActive) {
        content = {
          image: '/images/empty/no-data-found.png',
          title: 'No Products Available',
          subTitle: 'We couldn’t find any products that match your request.'
        };
      }

      return (
        <CenterBox height="100%">
          <SEmptyState
            title={content?.title}
            imageSrc={content?.image}
            subTitle={content?.subTitle}
          />
          {!!feedback && feedback}
        </CenterBox>
      );
    }

    return (
      <>
        <div
          ref={containerRef}
          style={{
            flex: 1,
            height: '100%',
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
            scrollBehavior: 'smooth'
          }}>
          {currentOpenView === Views.GRID_VIEW ? (
            <>
              {products.length > 0 && (
                <SVirtualGrid
                  grid={matrix as ProductType[][]}
                  vList={listVirtualizer?.getVirtualItems()}
                  height={listVirtualizer?.getTotalSize()}
                  vGrid={columnVirtualizer?.getVirtualItems()}
                  containerClass={classes.grid}
                  keyGetter={productKeyGetter}
                  fetchMore={aiSearchActive ? undefined : fetchMore}
                  render={product => {
                    const isChecked = selectedProducts.some(
                      i => productKeyGetter(i) === productKeyGetter(product)
                    );

                    return (
                      <Product
                        type={type}
                        badge={gridCard?.renderBadge?.(product)}
                        footer={gridCard?.renderFooter?.(product)}
                        hideStatusBadges={gridCard?.hideStatusBadges}
                        onProductClick={openProductDetails}
                        onPromotionClick={onPromotionClick}
                        onGetProductPrice={p => onGetProductPrice([p])}
                        productPricesLoading={productPricesLoading}
                        product={product}
                        selectedCustomer={selectedCustomer}
                        priceType={priceType}
                        stockRefresh={stock.refresh}
                        aiSearchActive={aiSearchActive}
                        gridConfig={{ ...grid, pricing: pricingList }}
                        stock={stock}
                        updateProduct={updateProduct}
                        onStockClick={i =>
                          openProductDetails(i, ProductDetailsTabsIds.WAREHOUSE)
                        }
                        searchQuery={searchQuery}
                        checkboxDisabled={() => disableSelect && !isChecked}
                        {...(disableSelect &&
                          !isChecked && {
                            checkboxTooltip: disableSelectProductTooltip
                          })}
                        checked={isChecked}
                        handleCheck={(checked, p) => {
                          handleProductSelect(checked, p);
                        }}
                      />
                    );
                  }}
                />
              )}
              {isLoading && <GridLoader gap={spacing(3)} itemWidth={272} />}
            </>
          ) : (
            <>
              <SVirtualTable<ProductType>
                stripped
                newDesign
                list={products}
                header={headerProps}
                showEmptyMessage={false}
                vList={listVirtualizer?.getVirtualItems()}
                height={listVirtualizer?.getTotalSize()}
                columns={columns}
                keyGetter={productKeyGetter}
                searchQuery={searchQuery}
                type={type}
                row={{
                  fallback: (
                    <TableLoader
                      rows={1}
                      height={listItemHeight + additionalContentHeight}
                    />
                  ),
                  onClick: openProductDetails,
                  rowStyleGetter: () => ({
                    maxHeight: listItemHeight + additionalContentHeight
                  }),
                  checkbox: product => {
                    const checked = selectedProducts.some(
                      i => productKeyGetter(i) === productKeyGetter(product)
                    );
                    const disabled = disableSelect && !checked;
                    return {
                      checked,
                      onChange: (_e, isChecked) => {
                        handleProductSelect(isChecked, [product]);
                      },
                      disabled,
                      tooltip: disabled && disableSelectProductTooltip
                    };
                  },
                  ...(aiSearchActive
                    ? {
                        additionalContent: product => (
                          <AdditionalContent
                            prefix={`${AIProductMatchTitle}:`}
                            percentage={product?.percentage}
                            text={product?.reasoning}
                          />
                        )
                      }
                    : {})
                }}
                infiniteScroll={{
                  fetchMore: aiSearchActive ? undefined : fetchMore
                }}
              />
              {isLoading && (
                <TableLoader
                  height={listItemHeight + additionalContentHeight}
                  columns={columns}
                />
              )}
            </>
          )}
          {canShowActionBar && type !== 'radio' && (
            <ActionBar
              selectedProducts={selectedProducts}
              buttonLabel={buttonLabel}
              handleEmail={handleEmail}
              handleSelected={() => setDrawerOpen(!drawerOpen)}
              handleClose={() => handleProductSelect(false, selectedProducts)}
            />
          )}
        </div>
        <SDrawer
          open={drawerOpen}
          style={{ position: 'fixed', zIndex: '1500' }}>
          <ProductDrawerContent
            products={selectedProducts}
            columns={configColumns}
            priceType={priceType}
            selectedCustomer={selectedCustomer}
            priceLoading={productPricesLoading}
            handleClose={() => setDrawerOpen(false)}
            getProductPrice={onGetProductPrice}
            buttonLabel={buttonLabel}
            clearAll={() => {
              handleProductSelect(false, selectedProducts);
              setDrawerOpen(false);
            }}
            handleEmail={() => {
              handleEmail();
              setDrawerOpen(false);
            }}
            handleRemoveProduct={product => {
              selectedProducts.length === 1 && setDrawerOpen(false);
              handleProductSelect(false, [product]);
            }}
          />
        </SDrawer>
        {!!feedback && feedback}
      </>
    );
  }
);
